import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import config from '../../config/config';
import * as XLSX from "xlsx";
import LoggingService from "../../services/LoggingService";
import "../../styles/Reports.css";
import { useTenant } from '../../context/TenantContext';
import { toast } from 'react-toastify';

const Reports = () => {
    const { selectedTenant } = useTenant();
    const [error, setError] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [robots, setRobots] = useState([]);
    const [selectedRobot, setSelectedRobot] = useState("");
    const [dateRange, setDateRange] = useState({ start: "", end: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize] = useState(50);

    const resetFilters = () => {
        setSelectedRobot("");
        setDateRange({ start: "", end: "" });
        setCurrentPage(1);
        setJobs([]);
        LoggingService.logAction("Filter Reset", "All filters have been reset");
        fetchJobs();
    };

    const fetchJobs = useCallback(async () => {
        const tenantId = selectedTenant?.orch_tenant_id || selectedTenant?.id;

        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('userToken');
            if (!token) {
                setError('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            const startDate = dateRange.start 
                ? new Date(new Date(dateRange.start).setHours(0, 0, 0, 0)).toISOString()
                : undefined;
            const endDate = dateRange.end 
                ? new Date(new Date(dateRange.end).setHours(23, 59, 59, 999)).toISOString()
                : undefined;

            const response = await axios.get(`${config.API_BASE_URL}/api/reports`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    robot: selectedRobot || undefined,
                    startTimeFrom: startDate,
                    startTimeTo: endDate,
                    page: currentPage,
                    limit: pageSize,
                    tenantId               // 🔑 EKLENDİ
                },
                timeout: 30000
            });

            if (!response.data?.jobs || !Array.isArray(response.data.jobs)) {
                throw new Error('Invalid response format');
            }

            const decryptedJobs = response.data.jobs.map(job => ({
                ...job,
                info: job.info || '',
                process: job.process || '',
                hostname: job.hostname || ''
            }));

            setJobs(decryptedJobs);
            setRobots(response.data.robots || []);
            setTotalPages(response.data.pagination?.totalPages || 1);
            setTotalRecords(response.data.pagination?.total || 0);

        } catch (error) {
            console.error("Error fetching jobs:", error);
            setError(error.response?.data?.details || 'Failed to fetch reports');
            setJobs([]);
            setRobots([]);
            toast.error(error.response?.data?.details || 'Error fetching reports');
        }
        setLoading(false);
    }, [selectedRobot, dateRange, selectedTenant?.id, currentPage, pageSize]);

    useEffect(() => {
        if (selectedTenant?.id) {
            fetchJobs();
        }
    }, [selectedTenant?.id, selectedRobot, dateRange, currentPage, pageSize, fetchJobs]);

    const handleDateRangeChange = (type, value) => {
        const newDateRange = { ...dateRange, [type]: value };
        if (newDateRange.start && newDateRange.end) {
            const start = new Date(newDateRange.start);
            const end = new Date(newDateRange.end);
            if (end < start) {
                toast.error('End date cannot be earlier than start date');
                return;
            }
            const sixMonths = 180 * 24 * 60 * 60 * 1000;
            if (end - start > sixMonths) {
                toast.warning('Date range cannot exceed 6 months');
                return;
            }
        }
        setDateRange(newDateRange);
        setCurrentPage(1);
    };

    const calculateMaxDate = (startDate) => {
        if (!startDate) return "";
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + 180);
        return maxDate.toISOString().split('T')[0];
    };

    const handleRobotChange = (e) => {
        setSelectedRobot(e.target.value);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && !loading) {
            setCurrentPage(newPage);
            fetchJobs();
        }
    };

    const exportToExcel = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const tenantId = selectedTenant?.orch_tenant_id || selectedTenant?.id;
            
            if (!token) {
                toast.error('Authentication token not found. Please login again.');
                return;
            }

            const startDate = dateRange.start 
                ? new Date(new Date(dateRange.start).setHours(0, 0, 0, 0)).toISOString()
                : undefined;
            const endDate = dateRange.end 
                ? new Date(new Date(dateRange.end).setHours(23, 59, 59, 999)).toISOString()
                : undefined;

            const response = await axios.get(`${config.API_BASE_URL}/api/reports/export`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    robot: selectedRobot || undefined,
                    startTimeFrom: startDate,
                    startTimeTo: endDate,
                    tenantId              // 🔑 EKLENDİ
                },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });

            const now = new Date();
            const fileName = `AutonomieReport_${now.toISOString().replace(/[:.]/g, '-')}.xlsx`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Export completed successfully');
        } catch (error) {
            console.error('Export error:', error);
            toast.error(error.response?.data?.message || 'Failed to export data');
        }
    };

    return (
        <div className="reports-container">
            <Helmet>
                <title>Reports - Autonomie</title>
            </Helmet>
            <h2>Reports</h2>

            <div className="filters">
                <label>Start Time Range:</label>
                <input 
                    type="date" 
                    value={dateRange.start} 
                    onChange={(e) => handleDateRangeChange('start', e.target.value)} 
                />
                <input 
                    type="date" 
                    value={dateRange.end} 
                    min={dateRange.start || ""} 
                    max={calculateMaxDate(dateRange.start)}
                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                    disabled={!dateRange.start} 
                />
                
                <label>Hostname:</label>
                <select value={selectedRobot} onChange={handleRobotChange}>
                    <option value="">All Hostnames</option>
                    {Array.isArray(robots) && robots.map((robot, index) => (
                        <option key={index} value={robot}>{robot}</option>
                    ))}
                </select>
            </div>
            <div className="buttons">
                <button onClick={resetFilters}>Reset</button>
                <button onClick={exportToExcel}>Export to Excel</button>
            </div>

            {error && error !== "Please select a tenant first" ? (
                <div className="error-message">{error}</div>
            ) : loading && jobs.length === 0 ? (
                <div className="loading-spinner">Loading...</div>
            ) : jobs.length === 0 && (dateRange.start || dateRange.end || selectedRobot) ? (
                <div className="no-data-message">No reports found for the selected criteria</div>
            ) : (
                <>
                    <div className="pagination-info">
                        <span>Total Records: {totalRecords}</span>
                    </div>

                    <table className="reports-table">
                        <thead>
                            <tr>
                                <th>Process</th>
                                <th>Hostname</th>
                                <th>State</th>
                                <th>Creation Time</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Duration</th>
                                <th>Info</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => (
                                <tr key={job.id}>
                                    <td>{job.process}</td>
                                    <td>{job.hostname}</td>
                                    <td>{job.state}</td>
                                    <td>{job.creationTime}</td>
                                    <td>{job.startTime}</td>
                                    <td>{job.endTime}</td>
                                    <td>{job.duration}</td>
                                    <td title={job.info}>
                                        {job.info.length > 50 ? `${job.info.substring(0, 24)}...` : job.info}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="pagination-controls">
                        <button 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                        >
                            Previous
                        </button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Reports;





// YENI TOKEN ALMA YONTEMI ONCESINDEKI KOD - DEGISTIRILME TARIHI 29.08.2025
// import React, { useState, useEffect, useCallback } from "react";
// import { Helmet } from "react-helmet";
// import axios from "axios";
// import config from '../../config/config';
// import * as XLSX from "xlsx";
// import LoggingService from "../../services/LoggingService";
// import "../../styles/Reports.css";
// import { useTenant } from '../../context/TenantContext';
// import { toast } from 'react-toastify';

// const Reports = () => {
//     const { selectedTenant } = useTenant();
//     const [error, setError] = useState(null);
//     const [jobs, setJobs] = useState([]);
//     const [loading, setLoading] = useState(false);  // Change initial state to false
//     const [robots, setRobots] = useState([]);
//     const [selectedRobot, setSelectedRobot] = useState("");
//     const [dateRange, setDateRange] = useState({ start: "", end: "" });
//     const [currentPage, setCurrentPage] = useState(1);
//     const [totalPages, setTotalPages] = useState(1);
//     const [totalRecords, setTotalRecords] = useState(0);
//     const [pageSize] = useState(50);

//     const resetFilters = () => {
//         setSelectedRobot("");
//         setDateRange({ start: "", end: "" });
//         setCurrentPage(1);
//         setJobs([]); // Clear the table data
//         LoggingService.logAction("Filter Reset", "All filters have been reset");
//         fetchJobs(); // Fetch fresh data after reset
//     };

//     const fetchJobs = useCallback(async () => {
//         // Add console logs for date range debugging
//         console.log('Date Range in fetchJobs:', {
//             start: dateRange.start,
//             end: dateRange.end
//         });

//         const filterCriteria = {
//             dateRange: dateRange.start ? 
//                 (dateRange.end ? 
//                     `${dateRange.start} to ${dateRange.end}` : 
//                     `From ${dateRange.start}`
//                 ) : 'No date range',
//             robot: selectedRobot || 'All Robots',
//             page: currentPage,
//             tenant: selectedTenant?.name || 'Unknown Tenant'
//         };
        
//         LoggingService.logAction("Filter Criteria", 
//             `Applied filters - Date: ${filterCriteria.dateRange}, Robot: ${filterCriteria.robot}, Page: ${filterCriteria.page}`
//         );

//           // Add console log for API parameters
//           console.log('Filter Criteria:', filterCriteria);

//         setLoading(true);
//         setError(null);
        
//         try {
//             const token = localStorage.getItem('userToken');
//             if (!token) {
//                 LoggingService.logError("Auth Validation", "No authentication token found");
//                 setError('Authentication token not found. Please login again.');
//                 setLoading(false);
//                 return;
//             }

//             // Format dates properly for the API with timezone consideration
//             const startDate = dateRange.start ? 
//                 new Date(new Date(dateRange.start).setHours(0, 0, 0, 0)).toISOString() : undefined;
//             const endDate = dateRange.end ? 
//                 new Date(new Date(dateRange.end).setHours(23, 59, 59, 999)).toISOString() : undefined;

//             // Add console log for formatted dates
//             console.log('Formatted Dates for API:', {
//                 startTimeFrom: startDate,
//                 startTimeTo: endDate
//             });

//             const response = await axios.get(`${config.API_BASE_URL}/api/reports`, {
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                     'X-UIPATH-OrganizationUnitId': selectedTenant?.id,
//                     'Content-Type': 'application/json',
//                 },
//                 params: {
//                     robot: selectedRobot || undefined,
//                     startTimeFrom: startDate,
//                     startTimeTo: endDate,
//                     page: currentPage,
//                     limit: pageSize
//                 },
//                 timeout: 30000
//             });

//             // Add console log for API response
//             console.log('API Response:', {
//                 totalJobs: response.data?.jobs?.length,
//                 pagination: response.data?.pagination
//             });

//             if (!response.data?.jobs || !Array.isArray(response.data.jobs)) {
//                 LoggingService.logError("Response Validation", "Invalid response format from API");
//                 throw new Error('Invalid response format');
//             }
            
//             LoggingService.logAction("Data Retrieved", 
//                 `Found ${response.data.jobs.length} jobs for the applied filters`
//             );

//             const decryptedJobs = response.data.jobs.map(job => ({
//                 ...job,
//                 info: job.info || '',
//                 process: job.process || '',
//                 hostname: job.hostname || ''
//             }));

//             setJobs(decryptedJobs);
//             setRobots(response.data.robots || []);
//             setTotalPages(response.data.pagination?.totalPages || 1);
//             setTotalRecords(response.data.pagination?.total || 0);

//             LoggingService.logAction("Filter Results", 
//                 `Total records: ${response.data.pagination?.total || 0}, Pages: ${response.data.pagination?.totalPages || 1}`
//             );

//             setError(null);
//         } catch (error) {
//             LoggingService.logError("Fetch Error", 
//                 `Failed to fetch reports with filters - ${error.message}`
//             );
//             console.error("Error fetching jobs:", error);
//             setError(error.response?.data?.details || error.response?.data?.message || 'Failed to fetch reports');
//             setJobs([]);
//             setRobots([]);
//             toast.error(error.response?.data?.details || 'Error fetching reports');
//         }
//         setLoading(false);
//     }, [selectedRobot, dateRange, selectedTenant?.id, currentPage, pageSize]);

//     // Update useEffect to include all filter changes
//     useEffect(() => {
//         if (selectedTenant?.id) {
//             fetchJobs();
//         }
//     }, [selectedTenant?.id, selectedRobot, dateRange, currentPage, pageSize, fetchJobs]); // Add all dependencies

//     const handleDateRangeChange = (type, value) => {
//         // Add console log for date change
//         console.log('Date Change:', {
//             type,
//             value,
//             currentDateRange: dateRange
//         });

//         LoggingService.logAction("Date Filter", `${type} date changed to: ${value || 'cleared'}`);
//         const newDateRange = { ...dateRange, [type]: value };
        
//         // Validate the new date range before setting
//         if (newDateRange.start && newDateRange.end) {
//             const start = new Date(newDateRange.start);
//             const end = new Date(newDateRange.end);
            
//             // Add console log for date validation
//             console.log('Date Validation:', {
//                 start: start.toISOString(),
//                 end: end.toISOString(),
//                 isValid: end >= start
//             });
            
//             if (end < start) {
//                 toast.error('End date cannot be earlier than start date');
//                 return;
//             }
            
//             const sixMonths = 180 * 24 * 60 * 60 * 1000;
//             if (end - start > sixMonths) {
//                 toast.warning('Date range cannot exceed 6 months');
//                 return;
//             }
//         }
        
//         setDateRange(newDateRange);
//         setCurrentPage(1); // Reset to first page when filter changes
//     };

//     const calculateMaxDate = (startDate) => {
//         if (!startDate) return "";
//         const maxDate = new Date(startDate);
//         maxDate.setDate(maxDate.getDate() + 180);
//         return maxDate.toISOString().split('T')[0];
//     };

//     const handleRobotChange = (e) => {
//         const selectedValue = e.target.value;
//         LoggingService.logAction("Robot Filter", `Robot selection changed to: ${selectedValue || 'All Robots'}`);
//         setSelectedRobot(selectedValue);
//     };

//     const handlePageChange = (newPage) => {
//         if (newPage >= 1 && newPage <= totalPages && !loading) {
//             LoggingService.logAction("Pagination", `Changed to page ${newPage}`);
//             setCurrentPage(newPage);
//             fetchJobs(); // Add this line to fetch data when page changes
//         }
//     };

//     const exportToExcel = async () => {
//         try {
//             LoggingService.logAction("Export", "Starting Excel export");
//             const token = localStorage.getItem('userToken');
            
//             if (!token) {
//                 toast.error('Authentication token not found. Please login again.');
//                 return;
//             }

//             // Format dates properly for the API with timezone consideration
//             const startDate = dateRange.start ? 
//                 new Date(new Date(dateRange.start).setHours(0, 0, 0, 0)).toISOString() : undefined;
//             const endDate = dateRange.end ? 
//                 new Date(new Date(dateRange.end).setHours(23, 59, 59, 999)).toISOString() : undefined;

//             const response = await axios.get(`${config.API_BASE_URL}/api/reports/export`, {
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                     'X-UIPATH-OrganizationUnitId': selectedTenant?.id,
//                     'Content-Type': 'application/json',
//                 },
//                 params: {
//                     robot: selectedRobot || undefined,
//                     startTimeFrom: startDate,
//                     startTimeTo: endDate
//                 },
//                 responseType: 'blob'
//             });

//             // Create a blob from the response data
//             const blob = new Blob([response.data], { 
//                 type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
//             });

//             // Format the current date for the filename
//             const now = new Date();
//             const day = String(now.getDate()).padStart(2, '0');
//             const month = String(now.getMonth() + 1).padStart(2, '0');
//             const year = now.getFullYear();
//             const hours = String(now.getHours()).padStart(2, '0');
//             const seconds = String(now.getSeconds()).padStart(2, '0');
            
//             // Include date range in filename if filters are applied
//             let fileName = `AutonomieReport_${day}${month}${year}_${hours}${seconds}`;
//             if (dateRange.start && dateRange.end) {
//                 const startFormatted = dateRange.start.replace(/-/g, '');
//                 const endFormatted = dateRange.end.replace(/-/g, '');
//                 fileName += `_${startFormatted}_${endFormatted}`;
//             }
//             fileName += '.xlsx';

//             // Create a download link and trigger download
//             const url = window.URL.createObjectURL(blob);
//             const link = document.createElement('a');
//             link.href = url;
//             link.setAttribute('download', fileName);
//             document.body.appendChild(link);
//             link.click();
//             link.remove();
//             window.URL.revokeObjectURL(url);
            
//             LoggingService.logAction("Export", "Excel export completed successfully");
//             toast.success('Export completed successfully');
//         } catch (error) {
//             LoggingService.logError("Export Error", `Failed to export to Excel: ${error.message}`);
//             console.error('Export error:', error);
//             toast.error(error.response?.data?.message || 'Failed to export data to Excel');
//         }
//     };

//     // Remove the handleFilter function since we're using auto-filtering

//     return (
//         <div className="reports-container">
//             <Helmet>
//                 <title>Reports - Autonomie</title>
//             </Helmet>
//             <h2>Reports</h2>

//             <div className="filters">
//                 <label>Start Time Range:</label>
//                 <input 
//                     type="date" 
//                     value={dateRange.start} 
//                     onChange={(e) => handleDateRangeChange('start', e.target.value)} 
//                 />
//                 <input 
//                     type="date" 
//                     value={dateRange.end} 
//                     min={dateRange.start || ""} 
//                     max={calculateMaxDate(dateRange.start)}
//                     onChange={(e) => handleDateRangeChange('end', e.target.value)}
//                     disabled={!dateRange.start} 
//                 />
                
//                 <label>Hostname:</label>
//                 <select value={selectedRobot} onChange={handleRobotChange}>
//                     <option value="">All Hostnames</option>
//                     {Array.isArray(robots) && robots.map((robot, index) => (
//                         <option key={index} value={robot}>{robot}</option>
//                     ))}
//                 </select>
//             </div>
//             <div className="buttons">
//                 <button onClick={resetFilters}>Reset</button>
//                 <button onClick={exportToExcel}>Export to Excel</button>
//             </div>

//             {error && error !== "Please select a tenant first" ? (
//                 <div className="error-message">
//                     {error}
//                 </div>
//             ) : loading && jobs.length === 0 ? (
//                 <div className="loading-spinner">
//                     Loading...
//                 </div>
//             ) : jobs.length === 0 && (dateRange.start || dateRange.end || selectedRobot) ? (
//                 <div className="no-data-message">
//                     No reports found for the selected criteria
//                 </div>
//             ) : (
//                 <>
//                     <div className="pagination-info">
//                         <span>Total Records: {totalRecords}</span>
//                     </div>

//                     <table className="reports-table">
//                         <thead>
//                             <tr>
//                                 <th>Process</th>
//                                 <th>Hostname</th>
//                                 <th>State</th>
//                                 <th>Creation Time</th>
//                                 <th>Start Time</th>
//                                 <th>End Time</th>
//                                 <th>Duration</th>
//                                 <th>Info</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {jobs.map((job) => (
//                                 <tr key={job.id}>
//                                     <td>{job.process}</td>
//                                     <td>{job.hostname}</td>
//                                     <td>{job.state}</td>
//                                     <td>{job.creationTime}</td>
//                                     <td>{job.startTime}</td>
//                                     <td>{job.endTime}</td>
//                                     <td>{job.duration}</td>
//                                     <td title={job.info}>
//                                         {job.info.length > 50 ? `${job.info.substring(0, 24)}...` : job.info}
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>

//                     <div className="pagination-controls">
//                         <button 
//                             onClick={() => handlePageChange(currentPage - 1)}
//                             disabled={currentPage === 1 || loading}
//                         >
//                             Previous
//                         </button>
//                         <span>Page {currentPage} of {totalPages}</span>
//                         <button 
//                             onClick={() => handlePageChange(currentPage + 1)}
//                             disabled={currentPage === totalPages || loading}
//                         >
//                             Next
//                         </button>
//                     </div>
//                 </>
//             )}
//         </div>
//     );
// };

// export default Reports;


